import { roundToDecimals } from '@principle-theorem/accounting';
import {
  AppointmentStatus,
  IMeasureMetadata,
  MeasureDataType,
  MeasureFormatter,
  MeasureIcon,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAppointmentDimension,
  IMeasureRef,
  IReportingQuery,
  MeasureTransformMap,
} from '@principle-theorem/reporting/interfaces';
import { AtLeast, ISO_TIME_FORMAT } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { QueryOptionResolver } from '../../query-option-resolvers';
import { BaseDimensionMeasures } from '../base-measures';
import { DataAccessorFactory, MeasurePath } from '../data-accessor-factory';
import {
  NullMeasureFilter,
  ValueEqualsMeasureFilter,
} from '../measure-filters';
import {
  CanBeChartedProperty,
  CanDoAllProperty,
  CanQueryByTimestampProperty,
  basicFilterAccessor,
  noFilterAccessor,
} from '../measure-properties';
import {
  MeasurePropertyFactory,
  defaultMetadata,
} from '../measure-property-factory';

export class AppointmentDimensionMeasures
  extends BaseDimensionMeasures
  implements MeasureTransformMap<Pick<IAppointmentDimension, 'status'>>
{
  get ref(): CanDoAllProperty {
    const propertyName = 'ref';
    const measure = this.measureRef(MeasurePath.docRef(propertyName));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Appointment ID',
        dataType: MeasureDataType.String,
        isFilterable: false,
        availableAsColumn: true,
        availableAsChart: false,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get practice(): CanDoAllProperty {
    const measure = this.measureRef('practice.name');
    return MeasurePropertyFactory.string(
      {
        id: this._pathWithPrefix('practice.name'),
        label: 'Practice Name',
        summary:
          'The name of the practice that the appointment was performed at',
        optionResolver: QueryOptionResolver.practices(),
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get invoiceRef(): CanDoAllProperty {
    const propertyName = 'invoiceRef';
    const measure = this.measureRef(MeasurePath.docRef(propertyName));
    return MeasurePropertyFactory.docRef(
      {
        id: this._pathWithPrefix(propertyName),
        label: 'Invoice Ref',
        dataType: MeasureDataType.String,
        isFilterable: false,
        availableAsColumn: false,
        availableAsChart: false,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get status(): CanDoAllProperty {
    const measure = this.measureRef('status');
    return MeasurePropertyFactory.enum<AppointmentStatus>(
      {
        id: this._pathWithPrefix('status'),
        label: 'Appointment Status',
        summary: 'The status of the appointment',
        optionResolver: QueryOptionResolver.enum(AppointmentStatus),
        icon: MeasureIcon.List,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      AppointmentStatus.Unscheduled
    );
  }

  get isBookedOnline(): CanDoAllProperty {
    const measure = this.measureRef('appointmentRequest');
    return MeasurePropertyFactory.propertyExists(
      {
        id: this._pathWithPrefix('isBookedOnline'),
        label: 'Is Booked Online',
        summary: 'Whether the appointment was booked online or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get confirmedAt(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('confirmedAt'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('confirmedAt'),
        label: 'Date Confirmed',
        summary: 'Date and time that the appointment was confirmed',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get firstBookedAt(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('firstBookedAt'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('firstBookedAt'),
        label: 'Date First Booked At',
        summary:
          'Date and time that the appointment was first booked in. This will be the same as the last booked date if the appointment was only booked once.',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get appointmentFirstBookedLeadTime(): CanBeChartedProperty {
    const propertyName = 'appointmentFirstBookedLeadTime';
    const firstBookedMeasure = this.measureRef(
      MeasurePath.timestamp('firstBookedAt')
    );
    const eventMeasure = this.measureRef(MeasurePath.timestamp('event.from'));
    return new CanBeChartedProperty({
      metadata: defaultMetadata({
        id: propertyName,
        label: 'Appointment First Booked Lead Time',
        summary:
          'The lead time between when the appointment was first booked, to when it was performed,',
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Suffix,
        formatterValue: 'days',
        icon: MeasureIcon.Numbers,
      }),
      measure: {
        propertyName,
        query: this.buildQuery()
          .attributes([
            firstBookedMeasure.attributePath,
            eventMeasure.attributePath,
          ])
          .get(),
        dataAccessor: (fact) => {
          const firstBooked = DataAccessorFactory.timestamp(
            firstBookedMeasure.factPropertyPath,
            undefined
          )(fact);
          const event = DataAccessorFactory.timestamp(
            eventMeasure.factPropertyPath,
            undefined
          )(fact);

          if (!firstBooked || !event) {
            return 0;
          }

          const diffInDays = moment(firstBooked).diff(moment(event), 'days');
          return roundToDecimals(Math.abs(diffInDays), 1);
        },
        filterAccessor: noFilterAccessor(),
      },
    });
  }

  get lastBookedAt(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('lastBookedAt'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('lastBookedAt'),
        label: 'Date Last Booked At',
        summary:
          'Date and time that the appointment was last booked in. This will be the same as the first booked date if the appointment was only booked once.',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get appointmentLastBookedLeadTime(): CanBeChartedProperty {
    const propertyName = 'appointmentLastBookedLeadTime';
    const lastBookedMeasure = this.measureRef(
      MeasurePath.timestamp('lastBookedAt')
    );
    const eventMeasure = this.measureRef(MeasurePath.timestamp('event.from'));
    return new CanBeChartedProperty({
      metadata: defaultMetadata({
        id: propertyName,
        label: 'Appointment Last Booked Lead Time',
        summary:
          'The lead time between when the appointment booking was last changed, to when it was performed. This might be the same as the last booked lead time if the appointment was only booked once.',
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Suffix,
        formatterValue: 'days',
        icon: MeasureIcon.Numbers,
      }),
      measure: {
        propertyName,
        query: this.buildQuery()
          .attributes([
            lastBookedMeasure.attributePath,
            eventMeasure.attributePath,
          ])
          .get(),
        dataAccessor: (fact) => {
          const lastBooked = DataAccessorFactory.timestamp(
            lastBookedMeasure.factPropertyPath,
            undefined
          )(fact);
          const event = DataAccessorFactory.timestamp(
            eventMeasure.factPropertyPath,
            undefined
          )(fact);

          if (!lastBooked || !event) {
            return '';
          }

          return Math.abs(moment(lastBooked).diff(moment(event), 'days'));
        },
        filterAccessor: noFilterAccessor(),
      },
    });
  }

  get dateBookedOnline(): CanQueryByTimestampProperty {
    const measure = this.measureRef(
      MeasurePath.timestamp('appointmentRequest.bookedAt')
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('dateBookedOnline'),
        label: 'Date Booked Online',
        summary: 'Date that the appointment was booked online',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }

  get bookedOnline(): CanDoAllProperty {
    return this.isBookedOnline
      .clone()
      .setMetadata({ id: this._pathWithPrefix('bookedOnline') })
      .filterBy(new NullMeasureFilter(false));
  }

  get notBookedOnline(): CanDoAllProperty {
    return this.isBookedOnline
      .clone()
      .setMetadata({ id: this._pathWithPrefix('notBookedOnline') })
      .filterBy(new NullMeasureFilter(true));
  }

  get tags(): CanDoAllProperty {
    const measure = this.measureRef('tags');
    return MeasurePropertyFactory.arrayRef(
      {
        id: this._pathWithPrefix('tags'),
        label: 'Appointment Tags',
        summary: 'The appointment tags that have been added to the appointment',
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Text,
        optionResolver: QueryOptionResolver.appointmentTags(),
        icon: MeasureIcon.Tag,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get date(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('event.from'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('date'),
        label: 'Appointment Date',
        summary:
          'Date that the appointment was performed. This is useful for grouping appointments by day.',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }

  get startTime(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('event.from'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('startTime'),
        label: 'Start Time',
        summary: 'Time that the appointment started at',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Time,
      ISO_TIME_FORMAT
    );
  }

  get endTime(): CanQueryByTimestampProperty {
    const measure = this.measureRef(MeasurePath.timestamp('event.to'));
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('endTime'),
        label: 'End Time',
        summary: 'Time that the appointment ended at',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Time,
      ISO_TIME_FORMAT
    );
  }

  get treatmentCategory(): CanDoAllProperty {
    const treatmentCategory = 'treatmentPlan.treatmentStep.treatmentCategory';
    const labelMeasure = this.measureRef(`${treatmentCategory}.name`);
    const colourMeasure = this.measureRef(`${treatmentCategory}.colour.value`);
    const dataMeasure = this.measureRef(
      MeasurePath.docRef(`${treatmentCategory}.ref`)
    );

    return AppointmentDimensionMeasureFactory.treatmentCategory(
      {
        id: this._pathWithPrefix('treatmentCategory'),
        label: 'Primary Treatment Category',
        summary:
          'The primary treatment category of the appointment. While an appointment may have treatments from multiple treatment categories, this is the primary category that the appointment is associated with, as seen on the Timeline.',
        optionResolver: QueryOptionResolver.treatmentCategories(),
        icon: MeasureIcon.List,
      },
      labelMeasure,
      colourMeasure,
      dataMeasure,
      this.buildQuery()
        .attributes([
          labelMeasure.attributePath,
          colourMeasure.attributePath,
          dataMeasure.attributePath,
        ])
        .get()
    );
  }

  get isBooked(): CanDoAllProperty {
    const metadata: IMeasureMetadata = defaultMetadata({
      id: this._pathWithPrefix('isBooked'),
      label: 'Is Booked',
      dataType: MeasureDataType.Boolean,
    });
    const isBooked = this.status
      .clone()
      .filterBy(
        new ValueEqualsMeasureFilter([
          AppointmentStatus.Scheduled,
          AppointmentStatus.Confirmed,
        ])
      );
    this._query = { ...this._query, ...isBooked };
    return new CanDoAllProperty({
      ...isBooked,
      metadata,
    });
  }

  get hasFollowUp(): CanDoAllProperty {
    const measure = this.measureRef(
      MeasurePath.timestamp('activeFollowUp.followUpDate')
    );
    return MeasurePropertyFactory.propertyExists(
      {
        id: this._pathWithPrefix('hasFollowUp'),
        label: 'Has Follow Up',
        summary: 'Whether the appointment has a follow up set or not',
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get()
    );
  }

  get followUpDate(): CanQueryByTimestampProperty {
    const measure = this.measureRef(
      MeasurePath.timestamp('activeFollowUp.followUpDate')
    );
    return MeasurePropertyFactory.timestamp(
      {
        id: this._pathWithPrefix('followUpDate'),
        label: 'Follow Up Date',
        summary:
          'This date of the follow up that has been set for this appointment. This is only set if the appointment has a follow up.',
        formatter: MeasureFormatter.Day,
      },
      measure,
      this.buildQuery().attributes([measure.attributePath]).get(),
      MeasureFormatter.Day
    );
  }
}

export class AppointmentDimensionMeasureFactory {
  static treatmentCategory(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    labelMeasure: IMeasureRef,
    colourMeasure: IMeasureRef,
    dataMeasure: IMeasureRef,
    measureQuery: IReportingQuery,
    defaultCategory: string = 'No Category'
  ): CanDoAllProperty {
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        optionResolver: QueryOptionResolver.treatmentCategories(),
        ...metadata,
      }),
      measure: {
        propertyName: labelMeasure.attributePath,
        query: measureQuery,
        dataAccessor: DataAccessorFactory.property(
          dataMeasure.factPropertyPath,
          defaultCategory
        ),
        labelAccessor: DataAccessorFactory.property(
          labelMeasure.factPropertyPath,
          defaultCategory
        ),
        filterAccessor: basicFilterAccessor(
          dataMeasure.factPropertyPath,
          undefined
        ),
      },
      groupMeasure: {
        queryAttributes: [
          labelMeasure.attributePath,
          colourMeasure.attributePath,
          dataMeasure.attributePath,
        ],
        dataAccessor: DataAccessorFactory.property(
          dataMeasure.factPropertyPath,
          defaultCategory
        ),
        labelAccessor: DataAccessorFactory.property(
          labelMeasure.factPropertyPath,
          defaultCategory
        ),
        colourAccessor: DataAccessorFactory.property(
          colourMeasure.factPropertyPath,
          '#CCC'
        ),
      },
    });
  }
}
